<template>
	<div>
		<v-dialog
			scrollable
			:value="isVisible"
			transition="dialog-bottom-transition"
			max-width="600"
			@click:outside="handleClickOutside"
			@keydown.esc="handleClickOutside"
			persistent
		>
			<v-card>
				<v-card-title class="pa-0">
					<v-toolbar :color="`${isDark ? '' : 'grey lighten-3'}`" flat dense>
						<v-toolbar-title>
							{{ $t('label.kolayik.bulk-kolayik-title') }}
						</v-toolbar-title>
						<v-spacer></v-spacer>
					</v-toolbar>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text style="padding: 0">
					<v-container fluid class="pt-3 pb-0">
						<v-alert dense type="info" text>
							{{ $t('label.kolayik.bulk-kolayik-description-paginated') }}
						</v-alert>
						<v-alert v-if="eligibleCandidates.length === 0" dense type="error" text>
							{{ $t('label.kolayik.bulk-kolayik-eligible-none') }}
						</v-alert>
						<v-alert v-else-if="skippedCount > 0" dense type="warning" text>
							{{
								$t('label.kolayik.bulk-kolayik-skipped', {
									count: skippedCount,
								})
							}}
						</v-alert>
						<template v-if="eligibleCandidates.length > 0 && stepForm && activeCandidate">
							<div class="bulk-kolay-step-meta">
								<span class="text-subtitle-2 font-weight-medium">{{
									$t('label.kolayik.bulk-kolayik-step-label', {
										current: stepIndex + 1,
										total: eligibleCandidates.length,
									})
								}}</span>
								<span class="bulk-kolay-candidate-name">{{ activeCandidate.name }}</span>
							</div>
							<v-progress-linear
								class="mt-2 mb-1"
								:value="stepProgress"
								color="primary"
								height="6"
								rounded
							></v-progress-linear>
						</template>
					</v-container>
					<ValidationObserver
						v-if="stepForm && activeCandidate"
						:key="'step-' + activeCandidate.id"
						ref="step_form"
						tag="form"
						id="bulk_step_form"
					>
						<v-container fluid>
							<v-row>
								<v-col>
									<ValidationProvider rules="required" v-slot="{ errors }">
										<v-text-field
											v-model="stepForm.kolay.firstName"
											:label="`${$t('label.kolayik.first-name')}*`"
											hide-no-data
											hide-details
											dense
											outlined
											chips
											deletable-chips
											class="v-select-talentics-chips"
											:error-messages="errors"
										></v-text-field>
									</ValidationProvider>
								</v-col>
								<v-col>
									<ValidationProvider rules="required" v-slot="{ errors }">
										<v-text-field
											v-model="stepForm.kolay.lastName"
											:label="`${$t('label.kolayik.last-name')}*`"
											hide-no-data
											hide-details
											dense
											outlined
											chips
											deletable-chips
											class="v-select-talentics-chips"
											:error-messages="errors"
										></v-text-field>
									</ValidationProvider>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-text-field
										v-model="stepForm.kolay.email"
										:label="$t('label.kolayik.email')"
										hide-no-data
										hide-details
										dense
										outlined
										chips
										deletable-chips
										class="v-select-talentics-chips"
									></v-text-field>
								</v-col>
								<v-col>
									<v-text-field
										v-model="stepForm.kolay.workEmail"
										:label="$t('label.kolayik.work-email')"
										hide-no-data
										hide-details
										dense
										outlined
										chips
										deletable-chips
										class="v-select-talentics-chips"
									></v-text-field>
								</v-col>
							</v-row>
							<v-row v-for="unit in stepForm.units" :key="'u-' + activeCandidate.id + '-' + unit.name">
								<v-col>
									<ValidationProvider
										:rules="
											(activeCandidate.company_id == 78 && unit.name == 'Birim') ||
											(activeCandidate.company_id == 133 && unit.name == 'Takım')
												? ''
												: 'required'
										"
										v-slot="{ errors }"
									>
										<v-select
											v-model="unit.value"
											:items="
												unit.items.map(o => ({
													text: o.name,
													value: o.name,
												}))
											"
											:label="`
                          ${$t(`label.kolayik.${unit.name}`)}
                          ${
												(activeCandidate.company_id == 78 &&
													unit.name == 'Birim') ||
												(activeCandidate.company_id == 133 &&
													unit.name == 'Takım')
													? ''
													: '*'
											}
                        `"
											outlined
											hide-details
											dense
											:error-messages="errors"
										></v-select>
									</ValidationProvider>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<label
										class="v-label v-label--active"
										style="font-size: 12px"
										>{{ $t('label.kolayik.manager') }}</label
									>
									<Autocomplete
										v-model="stepForm.manager"
										label=""
										:items="
											managers.length === 0 && stepForm.manager.id
												? [{ ...stepForm.manager }]
												: [...managers]
										"
										item-text="fullName"
										item-value="id"
										filled
										dense
										flat
										solo
										outlined
										hide-details
										:onKeyUp="handleManagerSearchInput"
										@change="handleManagerChange"
										:resetValueAfterChange="false"
									></Autocomplete>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-text-field
										v-model="stepForm.kolay.mobilePhone"
										:label="$t('label.kolayik.phone-personal')"
										outlined
										hide-details
										dense
									></v-text-field>
								</v-col>
								<v-col>
									<v-text-field
										v-model="stepForm.kolay.workPhone"
										:label="$t('label.kolayik.phone-work')"
										outlined
										hide-details
										dense
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<DatePicker
										v-model="stepForm.kolay.employmentStartDate"
										@input="handleEmploymentStartDate"
										:label="$t('label.kolayik.employment-start')"
										full-width
										no-title
										reactive
										dense
									></DatePicker>
								</v-col>
								<v-col>
									<DatePicker
										v-model="stepForm.kolay.birthday"
										@input="handleBirthday"
										:label="$t('label.kolayik.birthday')"
										full-width
										no-title
										reactive
										dense
									></DatePicker>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-text-field
										v-model="stepForm.kolay.educationalInstitution"
										:label="$t('label.kolayik.education')"
										outlined
										hide-details
										dense
									></v-text-field>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.educationLevel"
										:items="
											educationLevels.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.education-level')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.educationStatus"
										:items="
											educationStatuses.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.education-status')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-select
										v-model="stepForm.kolay.bloodType"
										:items="
											bloodTypes.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.blood-types')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.accessType"
										:items="
											accessTypes.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.access-types')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-select
										v-model="stepForm.kolay.contractType"
										:items="
											contractTypes.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.contract-type')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
								<v-col v-if="stepForm.kolay.contractType === 'definite'">
									<ValidationProvider rules="required" v-slot="{ errors }">
										<DatePicker
											v-model="stepForm.kolay.contractEndDate"
											@input="handleContractEndDate"
											:label="$t('label.kolayik.contract-end-date')"
											full-width
											no-title
											reactive
											dense
											:error-messages="errors"
										></DatePicker>
									</ValidationProvider>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.gender"
										:items="
											genders.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.gender')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
							</v-row>
							<v-row v-if="stepForm.kolay.militaryService === 'delayed'">
								<v-col>
									<DatePicker
										v-model="stepForm.kolay.militaryServiceDelayDate"
										@input="handleMilitaryServiceDelayDate"
										:label="$t('label.kolayik.military-delay-date')"
										full-width
										no-title
										reactive
										dense
									></DatePicker>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-select
										v-model="stepForm.kolay.levelOfDisability"
										:items="
											levelsOfDisability.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.disability-level')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.maritalStatus"
										:items="maritalStatus"
										:label="$t('label.kolayik.marital-status')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<CountryPicker
										v-model="stepForm.kolay.nationality"
										:label="$t('label.kolayik.nationality')"
									></CountryPicker>
								</v-col>
								<v-col>
									<v-select
										v-model="stepForm.kolay.militaryService"
										:items="
											militaryService.map(item => ({
												text: item.text,
												value: item.value,
											}))
										"
										:label="$t('label.kolayik.military-service')"
										outlined
										hide-details
										dense
									></v-select>
								</v-col>
								<v-col v-if="stepForm.kolay.nationality === 'TR'">
									<v-text-field
										v-model="stepForm.kolay.idNumber"
										:label="$t('label.kolayik.id-number')"
										hide-no-data
										hide-details
										dense
										outlined
										chips
										deletable-chips
										class="v-select-talentics-chips"
									></v-text-field>
								</v-col>
							</v-row>
						</v-container>
					</ValidationObserver>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-text v-if="submitError" class="pt-2 pb-0">
					<v-alert dense type="error" text>{{ submitError }}</v-alert>
				</v-card-text>
				<v-card-actions class="flex-wrap">
					<v-btn
						text
						:disabled="submitting || stepIndex === 0"
						@click="handlePrevious"
					>
						<v-icon left>keyboard_arrow_left</v-icon>
						{{ $t('button.prev') }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						text
						:disabled="submitting"
						@click="() => onClose({ refresh: false })"
					>
						{{ $t('button.cancel') }}
					</v-btn>
					<v-btn
						v-if="!isLastStep"
						color="primary"
						:disabled="submitting || eligibleCandidates.length === 0"
						@click="handleNext"
					>
						{{ $t('button.next') }}
						<v-icon right>keyboard_arrow_right</v-icon>
					</v-btn>
					<v-btn
						v-else
						color="primary"
						:loading="submitting"
						:disabled="eligibleCandidates.length === 0"
						@click="handleSubmit"
					>
						{{ $t('button.send') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import api from '@/api';
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import Autocomplete from '@/components/shared/autocomplete/Autocomplete';
import DatePicker from '@/components/shared/datePicker/DatePicker';
import CountryPicker from '@/components/shared/countryPicker/CountryPicker';
import { format } from 'date-fns';
import {
	getKolayRelatedFields,
	buildKolayPostPayload,
	isEligibleForBulkKolaySync,
	parseKolayBulkResponse,
} from '@/utils/kolayIkPayload';

function cloneJson(obj) {
	return JSON.parse(JSON.stringify(obj));
}

export default {
	name: 'BulkKolayIKDialog',
	components: {
		ValidationObserver,
		ValidationProvider,
		Autocomplete,
		DatePicker,
		CountryPicker,
	},
	data() {
		return {
			files: [],
			isVisible: true,
			submitting: false,
			submitError: null,
			hierarchyUnits: [],
			candidateSteps: {},
			stepIndex: 0,
			managers: [],
			bloodTypes: [
				{ text: 'A+', value: 'A+' },
				{ text: 'A-', value: 'A-' },
				{ text: 'B+', value: 'B+' },
				{ text: 'B-', value: 'B-' },
				{ text: 'AB+', value: 'AB+' },
				{ text: 'AB-', value: 'AB-' },
				{ text: '0', value: '0' },
			],
			accessTypes: [
				{
					text: this.$t(`label.access-type-employee`),
					value: 'employee',
				},
				{
					text: this.$t(`label.access-type-manager`),
					value: 'manager',
				},
				{
					text: this.$t(`label.access-type-owner`),
					value: 'owner',
				},
			],
			genders: [
				{ text: this.$t(`label.male`), value: 'male' },
				{ text: this.$t(`label.female`), value: 'female' },
			],
			educationLevels: [
				{ text: this.$t(`label.primary`), value: 'primary' },
				{ text: this.$t(`label.secondary`), value: 'secondary' },
				{ text: this.$t(`label.high_school`), value: 'high_school' },
				{ text: this.$t(`label.associate`), value: 'associate' },
				{ text: this.$t(`label.bachelor`), value: 'bachelor' },
				{ text: this.$t(`label.master`), value: 'master' },
				{ text: this.$t(`label.doctorate`), value: 'doctorate' },
				{ text: this.$t(`label.not-available`), value: 'none' },
			],
			contractTypes: [
				{ text: this.$t(`label.definite-contract`), value: 'definite' },
				{ text: this.$t(`label.indefinite-contract`), value: 'indefinite' },
			],
			levelsOfDisability: [
				{ text: this.$t(`label.not-disabled`), value: 'notDisabled' },
				{ text: this.$t(`label.level-1-disabled`), value: 'firstLevel' },
				{ text: this.$t(`label.level-2-disabled`), value: 'secondLevel' },
				{ text: this.$t(`label.level-3-disabled`), value: 'thirdLevel' },
			],
			maritalStatus: [
				{ text: this.$t(`label.single`), value: 'single' },
				{
					text: this.$t(`label.married`),
					value: 'married',
				},
				{
					text: this.$t(`label.divorced`),
					value: 'divorced',
				},
				{
					text: this.$t(`label.unspecified`),
					value: 'unspecified',
				},
			],
			militaryService: [
				{
					text: this.$t(`label.military-service-completed`),
					value: 'completed',
				},
				{
					text: this.$t(`label.military-service-not-completed`),
					value: 'uncompleted',
				},
				{ text: this.$t(`label.military-service-exempt`), value: 'exempt' },
				{ text: this.$t(`label.military-service-delayed`), value: 'delayed' },
				{ text: this.$t(`label.military-service-deserter`), value: 'deserter' },
				{
					text: this.$t(`label.military-service-draft-evader`),
					value: 'draftEvader',
				},
			],
			educationStatuses: [
				{ text: this.$t(`label.education-graduate`), value: 'graduate' },
				{ text: this.$t(`label.education-student`), value: 'student' },
			],
		};
	},
	computed: {
		...mapState({
			currentLang: state => state.currentUser.lang,
			isDark: state => state.currentUser.isDark,
			company: state => state.company.company,
		}),
		eligibleCandidates() {
			const { candidates } = this;
			if (!candidates || !candidates.length) {
				return [];
			}
			return candidates.filter(isEligibleForBulkKolaySync);
		},
		skippedCount() {
			const { candidates } = this;
			if (!candidates || !candidates.length) {
				return 0;
			}
			return candidates.filter(c => !isEligibleForBulkKolaySync(c)).length;
		},
		activeCandidate() {
			const list = this.eligibleCandidates;
			if (!list.length) {
				return null;
			}
			return list[this.stepIndex] || null;
		},
		stepForm() {
			const c = this.activeCandidate;
			if (!c) {
				return null;
			}
			return this.candidateSteps[c.id] || null;
		},
		isLastStep() {
			const n = this.eligibleCandidates.length;
			return n > 0 && this.stepIndex >= n - 1;
		},
		stepProgress() {
			const n = this.eligibleCandidates.length;
			if (!n) {
				return 0;
			}
			return ((this.stepIndex + 1) / n) * 100;
		},
	},
	props: {
		candidates: {
			type: Array,
			required: true,
		},
		onClose: {
			type: Function,
			required: true,
		},
	},
	methods: {
		getEmptyKolay() {
			return {
				firstName: null,
				lastName: null,
				email: null,
				workEmail: null,
				mobilePhone: null,
				workPhone: null,
				employmentStartDate: null,
				birthday: null,
				educationalInstitution: null,
				educationLevel: null,
				educationStatus: null,
				bloodType: null,
				accessType: null,
				contractType: 'indefinite',
				contractEndDate: null,
				gender: null,
				levelOfDisability: 'notDisabled',
				maritalStatus: null,
				nationality: 'TR',
				militaryService: null,
				militaryServiceDelayDate: null,
				idNumber: null,
			};
		},
		initCandidateSteps() {
			if (!this.hierarchyUnits || !this.hierarchyUnits.length) {
				this.candidateSteps = {};
				return;
			}
			const steps = {};
			this.eligibleCandidates.forEach(c => {
				const kolay = {
					...this.getEmptyKolay(),
					...getKolayRelatedFields(c),
				};
				steps[c.id] = {
					kolay,
					manager: {
						id: null,
						fullName: null,
					},
					units: cloneJson(this.hierarchyUnits),
				};
			});
			this.candidateSteps = steps;
			this.stepIndex = 0;
		},
		handleClickOutside(evt) {
			evt.preventDefault();
		},
		handleEmploymentStartDate(date) {
			if (this.stepForm && this.stepForm.kolay) {
				this.stepForm.kolay.employmentStartDate = format(
					new Date(date),
					'yyyy-MM-dd',
				);
			}
		},
		handleBirthday(date) {
			if (this.stepForm && this.stepForm.kolay) {
				this.stepForm.kolay.birthday = format(new Date(date), 'yyyy-MM-dd');
			}
		},
		handleContractEndDate(date) {
			if (this.stepForm && this.stepForm.kolay) {
				this.stepForm.kolay.contractEndDate = format(
					new Date(date),
					'yyyy-MM-dd',
				);
			}
		},
		handleMilitaryServiceDelayDate(date) {
			if (this.stepForm && this.stepForm.kolay) {
				this.stepForm.kolay.militaryServiceDelayDate = format(
					new Date(date),
					'yyyy-MM-dd',
				);
			}
		},
		handleManagerChange(newVal) {
			if (newVal && this.stepForm) {
				this.stepForm.manager = { ...newVal };
				this.managers = [];
			}
		},
		handleManagerSearchInput({ newVal }) {
			if (newVal && newVal.length > 1) {
				api.searchManager.get({ q: newVal }).then(response => {
					const { people } = response.data;
					this.managers = people.map(person => ({
						fullName: `${person.firstName + ' ' + person.lastName}`,
						id: person.id,
					}));
				});
			} else {
				this.managers = [];
			}
		},
		async handleNext() {
			const ok = await this.$refs.step_form.validate();
			if (ok && !this.isLastStep) {
				this.managers = [];
				this.stepIndex += 1;
			}
		},
		handlePrevious() {
			if (this.stepIndex > 0) {
				this.managers = [];
				this.stepIndex -= 1;
			}
		},
		async handleSubmit() {
			if (this.eligibleCandidates.length === 0) {
				return;
			}
			const isValid = await this.$refs.step_form.validate();
			if (!isValid) {
				return;
			}
			this.submitting = true;
			this.submitError = null;
			const { eligibleCandidates, candidateSteps } = this;
			const items = eligibleCandidates.map(candidate => {
				const entry = candidateSteps[candidate.id];
				const mergedKolay = {
					...getKolayRelatedFields(candidate),
					...entry.kolay,
				};
				const payload = buildKolayPostPayload({
					kolay: mergedKolay,
					manager: entry.manager,
					units: entry.units,
				});
				return { candidate_id: candidate.id, payload };
			});
			try {
				const response = await api.kolay.postBulk({
					items,
					talenticsSuppressGlobalSnackbar: true,
				});
				const { ok, failureDetails } = parseKolayBulkResponse(
					response,
					items.length,
					eligibleCandidates,
				);
				this.submitting = false;
				if (ok > 0) {
					this.onClose({
						refresh: true,
						bulkKolayOk: ok,
						bulkKolayFailureDetails: failureDetails,
					});
				} else {
					this.submitError = this.$t('label.kolayik.bulk-kolayik-all-failed');
				}
			} catch (err) {
				this.submitting = false;
				const data = err && err.response && err.response.data;
				const msg =
					(data && data.message) ||
					(data && data.data && data.data.message) ||
					(typeof data === 'string' ? data : null);
				this.submitError =
					msg || this.$t('snackbar-messages.an-error-occurred');
			}
		},
		async getAndSetHierarchy() {
			const res = await api.hierarchy.get({});
			this.hierarchyUnits = res.data.result || [];
		},
	},
	async mounted() {
		await this.getAndSetHierarchy();
		this.initCandidateSteps();
		setTimeout(
			function () {
				this.isVisible = true;
			}.bind(this),
			500,
		);
	},
};
</script>

<style lang="scss" scoped>
.bulk-kolay-step-meta {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.bulk-kolay-candidate-name {
	font-size: 1.05rem;
	font-weight: 600;
}
</style>
